import { ReactElement, ReactNode } from "react";

type CaseProps<T> = {
	value: T;
	children?: ReactNode;
};

type SwitchProps<T> = {
	expression: T;
	children: ReactElement<CaseProps<T>>[];
	default?: ReactNode
};

/**
 * A React component that mimics a switch statement's functionality
 * Evaluates an expression against multiple cases and renders matching content
 * @example
 * <Switch expression={status}>
 * 	<Case value="loading">
 * 		<LoadingSpinner />
 * 	</Case>
 * 	<Case value="error">
 * 		<ErrorMessage />
 * 	</Case>
 * 	<Case value="success">
 * 		<SuccessMessage />
 *   </Case>
 * </Switch>

 * 
 * @param children - Array of Case components to evaluate
 * @param expression - Value to match against Case values
 * @param defaultCase - Content to render if no cases match (optional)
 * 
 * @returns Matching case's children or default case if no match
 */
export function Switch<T>({ children, expression, default: defaultCase }: SwitchProps<T>): ReactNode | null {
	if (!children) {
		return defaultCase;
	}

	const matchingChild = children.find(child => child.props.value === expression);
	if (matchingChild) {
		return matchingChild.props.children;
	}

	return defaultCase;
}

/**
 * Case component to be used as a child of Switch
 * Represents a single case in the switch statement
 * 
 * @param children - Content to render if this case matches
 * @returns Children or null if no children provided
 */
export function Case<T>({ children }: CaseProps<T>): ReactNode | null {
	return children ?? null;
}
